<template>
    <div
        class="table-cell-wrapper"
        :data-row-id="`${data.id}`"
        :data-testid="`purchase-order-table-td-${index}-${column}`"
        :class="[`${column}-column`]"
    >
        <template v-if="column === 'status' && data.status">
            <TablePOStatusColumn :status="data.status" />
        </template>
        <div v-else class="table-cell-wrapper_content / flex">
            <div v-if="!isOverflowing" ref="cellContent" class="cellContent">
                {{ getCellLabel }}
            </div>
            <VJoyTooltip v-else variant="secondary" placement="bottom">
                <div ref="cellContent" class="cellContent">{{ getCellLabel }}</div>
                <template #tooltip-content>
                    <p>{{ getCellLabel }}</p>
                </template>
            </VJoyTooltip>
        </div>
    </div>
</template>

<script setup lang="ts">
    import {computed, watch, useTemplateRef, type Ref} from 'vue';
    import {useFormat, useTranslation} from '#imports';
    import {useContentOverflow} from '@malt/utils';
    import {VJoyTooltip} from '@maltjoy/core-vue';
    import type {PurchaseOrderMonitoringResource} from '@api';
    import TablePOStatusColumn from './TablePOStatusColumn.vue';
    import type {PurchaseOrderDataTableColumn} from '../../types';

    const {t} = useTranslation();
    const {formatAmount, formatDateToShort} = useFormat();

    const props = defineProps<{
        column: PurchaseOrderDataTableColumn;
        data: PurchaseOrderMonitoringResource;
        index: number;
    }>();

    const cellContent = useTemplateRef('cellContent');
    const {isOverflowing, startObserving, stopObserving} = useContentOverflow({root: cellContent as Ref<HTMLElement | undefined>, immediate: false});

    watch(
        isOverflowing,
        () => {
            stopObserving();
            startObserving();
        },
        {immediate: true},
    );

    const getCellLabel = computed((): string => {
        switch (props.column) {
            case 'amount':
                return formatAmount('EUR', props.data.amount ?? 0, {decimals: 2});
            case 'creationDate':
                return props.data.creationDate ? formatDateToShort(new Date(props.data.creationDate)) : '—';
            case 'label':
                return props.data.label ?? '—';
            default:
                return String(props.data[props.column] ?? '');
        }
    });
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .table-cell-wrapper {
        display: block;
        position: relative;
        max-width: 100%;
        width: 100%;
        height: 100%;
        padding: 0 var(--joy-core-spacing-4);

        &_content {
            height: 100%;
            align-items: center;

            .cellContent {
                max-width: 100%;
            }

            .joy-tooltip,
            .joy-tooltip__wrapper {
                max-width: 100%;
            }
        }

        &.identifier-column,
        &.label-column {
            max-width: 280px;
        }

        &.amount-column,
        &.creationDate-column {
            .table-cell-wrapper_content {
                justify-content: flex-end;
            }
        }
    }
</style>
